import {
  fetchGoalInfo,
  fetchGoalHomeSubscriptionData,
  fetchGoalSubscriptionFeatures,
  fetchGoalSecificCities
} from '@api/fetchers/goal';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import NewGoalLayout from '@layout/NewGoalLayout/NewGoalLayout';
import { SWRConfig } from 'swr';
import useFullGoalInfo from '@api/hooks/goal/useFullGoalInfo';

import NewSubscription from '@cont/NewSubscription/NewSubscription';
import useSubscribed from '@hooks/useSubscribed';
import { SUBSCRIPTION } from '@cont/NewPreSubscribed/constants';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import { fetchUserProfileInfo } from '@api/fetchers/store';
import { getSubsriptionFeaturesKey } from '@api/hooks/goal/useSubscriptionBenefits';
import { getGoalCitiesKey } from '@api/hooks/offlineCentres/useGoalSpecificCities';
import PlusHomePage from '../[goalUID]';

const SubscriptionPage = ({
  goalUID,
  // Pre-fetched data from the server
  fallbackData
}) => {
  const {
    isIconicAvailable,
    isIconicProAvailable,
    // eslint-disable-next-line no-unused-vars
    isPlusAvailable,
    isK12Goal: isK12,
    isPlatformGoal
  } = fallbackData?.goalInfo;
  const { keys, goalInfo } = useFullGoalInfo({
    isK12,
    isPlatformGoal,
    goalUID,
    isIconicAvailable,
    isIconicProAvailable,
    fallbackData
  });
  const isSubscribed = useSubscribed(goalUID);
  const subscriptionFeaturesKey = getSubsriptionFeaturesKey(
    goalUID,
    isIconicProAvailable
  );
  const goalCitiesKey = getGoalCitiesKey(goalUID);
  return (
    <SWRConfig
      value={{
        dedupingInterval: 60 * 1000,
        fallback: {
          [keys.goalInfoSWRKey]: fallbackData.goalInfo,
          [keys.staticContentSWRKey]: fallbackData.staticContent,
          [subscriptionFeaturesKey]: fallbackData.subscriptionFetaures,
          [goalCitiesKey]: fallbackData.goalSpecificCities
        }
      }}
    >
      <NewSubscription
        subscriptionData={fallbackData?.subscriptionData || []}
        isSubscribed={isSubscribed}
        isK12={isK12}
        goalDetails={goalInfo}
        isPlatformGoal={isPlatformGoal}
      />
    </SWRConfig>
  );
};

// CDN cache with a TTL of 4hrs is enabled for this page
export const getStaticProps = async ({ params, preview }) => {
  const { goalUID: routeGoalUID, goalSlug: querySlug } = params || {};
  const goalUID = routeGoalUID.toUpperCase();
  const goalInfoRes = await resolvePromiseToObject(
    fetchGoalInfo(goalUID),
    'goalInfo'
  ).catch(() => ({ type: 'goalInfo', result: {} }));

  const promises = [Promise.resolve(goalInfoRes)];
  promises.push(
    resolvePromiseToObject(
      fetchGoalHomeSubscriptionData(goalUID),
      'subscriptionData'
    )
  );
  const isIconicProAvailable = !!goalInfoRes?.result?.isIconicProAvailable;
  promises.push(
    resolvePromiseToObject(
      fetchGoalSubscriptionFeatures(goalUID, isIconicProAvailable),
      'subscriptionFetaures'
    )
  );

  promises.push(
    resolvePromiseToObject(
      fetchGoalSecificCities(goalUID),
      'goalSpecificCities'
    )
  );
  promises.push(
    resolvePromiseToObject(
      fetchPageMetaInfo(PlusHomePage.pageInfo.name, goalUID, preview),
      'metaInfo'
    )
  );

  promises.push(
    resolvePromiseToObject(fetchUserProfileInfo(goalUID), 'userProfile')
  );

  let fallbackData = {};
  if (promises.length > 0)
    fallbackData = await Promise.all(promises).then((resolvedVals) => {
      const resultObj = {};
      for (let i = 0; i < resolvedVals.length; i += 1)
        resultObj[resolvedVals[i].type] = resolvedVals[i].result;
      return resultObj;
    });
  const { metaInfo = {} } = fallbackData;
  return {
    props: {
      // Basic Data
      goalUID,
      querySlug,
      pageMetaInfo: {
        ...metaInfo,
        showStoreTab: fallbackData?.userProfile?.showStoreTab || false
      },
      // Info required to render page
      fallbackData
    },
    revalidate: 1 * 60
  };
};
export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};
SubscriptionPage.pageInfo = {
  name: SUBSCRIPTION,
  isMWebSupported: true,
  layoutOptions: {
    hideLearnerMinutes: true,
    disableDarkMode: true
  }
};

export default SubscriptionPage;

SubscriptionPage.Layout = NewGoalLayout;
